'use client';

import classNames from 'classnames';
import Link from 'next/link';
import { MouseEvent as ReactMouseEvent } from 'react';
import { Trans } from 'react-i18next';
import { formatPrice } from 'utils';

import { useTranslation } from '@/i18n/client';
import {
  clickProductListPageEvent,
  clickProductSearchPageEvent,
} from '@/modules/google/data-layer/data-layer';
import { ProductListItem } from '@/modules/product/api/productList';
import { useProductList } from '@/modules/product/context/productList';
import { useBasket } from '@/shared/context/basket-context';
import { routes } from '@/shared/data/routes';
import { useCurrentLocale } from '@/shared/utils/client';
import { productName } from '@/shared/utils/product-name';
import { renderShippingTime } from '@/shared/utils/shipping-time';

import { ProductSlider } from '../productSlider';
import styles from './ProductTile.module.scss';
import { ProductTileProps } from './types';

export const ProductTile = ({
  index,
  product,
  listId,
  listName,
  attributionToken,
  onClick,
  onAddToCartClick,
  ...props
}: ProductTileProps) => {
  const { freeShippingPrice } = useBasket();
  const { defaultCalendar, shippingDeliverySettings, page, limit } =
    useProductList();
  const locale = useCurrentLocale();
  const { t } = useTranslation('products');
  const href = `${routes[locale].SINGLE_PRODUCT}/${product.slug}`;

  const { shipmentTime, isDate } = renderShippingTime(
    product.shippingTime,
    locale,
    props.shippingDeliverySettings || shippingDeliverySettings,
    props.defaultCalendar || defaultCalendar
  );

  const handleProductClick = (
    e: ReactMouseEvent<HTMLAnchorElement | HTMLDivElement, MouseEvent>
  ) => {
    onClick?.(product);

    clickProductListPageEvent(
      product,
      listId,
      listName,
      product.currency,
      index,
      { page, limit }
    );

    clickProductSearchPageEvent(
      product,
      listId,
      listName,
      product.currency,
      index,
      { page, limit }
    );
  };

  function handleAddToCardClick(product: ProductListItem) {
    onAddToCartClick?.(product);
  }

  function handleProductSliderClick() {
    onClick?.(product);
  }

  const deliveryI18Key =
    product.price >= freeShippingPrice.amount
      ? isDate
        ? 'common.listFreeShipmentTimeIn'
        : 'common.listFreeShipmentTime'
      : isDate
        ? 'common.listShipmentTimeIn'
        : 'common.listShipmentTime';

  return (
    <div className={classNames(styles.productTile)}>
      <div
        className={classNames(styles['productTile__upper-section'], {
          [styles['opacity']]: product.stock === 0,
        })}
      >
        <ProductSlider
          href={href}
          images={product.images}
          index={index}
          listId={listId}
          listName={listName}
          attributionToken={attributionToken}
          product={product}
          onAddToCartClick={handleAddToCardClick}
          onClick={handleProductSliderClick}
        />
      </div>
      <div className={styles['productTile__bottom-section']}>
        <Link prefetch={false} href={href} onClick={handleProductClick}>
          <div className={styles['productTile__prices']}>
            <p className={styles['productTile__prices--main-price']}>
              {formatPrice(product.price, locale, product.currency)}
            </p>
            {product.previousPrice && product.previousPrice > 0 ? (
              <p className={styles['productTile__prices--promo-price']}>
                {formatPrice(product.previousPrice, locale, product.currency)}
              </p>
            ) : null}
          </div>
          <div className={styles.productTile__descriptionWrapper}>
            <p
              className={styles['productTile__description']}
              title={productName(product)}
            >
              {productName(product)}
            </p>
          </div>
          <hr className={styles['productTile__divider']} />
          <p className={styles['productTile__additional-description']}>
            {product.stock === 0 ? (
              <strong>{t('productList.item.out-of-stock')}</strong>
            ) : (
              <Trans
                i18nKey={deliveryI18Key}
                t={t}
                components={[<strong key={0} />]}
                values={{ time: t(shipmentTime) }}
              />
            )}
          </p>
        </Link>
      </div>
    </div>
  );
};
